import { useState } from "react";
import { Ban, FlaskConical, Plus, WalletCards } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, inr, useCan, useLoad } from "@/lib/api";
import { Empty, ErrorBox, PageHeading } from "@/components/common";
import LabCaseList from "@/components/LabCaseList";
import { LabCaseForm, LabPaymentForm } from "@/components/forms/LabForms";

function LabPayments({ rows, onChanged }) {
  const can = useCan();
  const voidIt = async (p) => {
    const reason = window.prompt("Reason for voiding this lab payment:");
    if (!reason) return;
    try { await api.post(`/lab/payments/${p.id}/void`, { reason }); toast.success("Lab payment voided"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!rows.length) return <Empty icon={WalletCards} title="No lab payments yet" text="Payments you make to labs appear here." testid="lab-payments-empty" />;
  return (
    <section className="table-card">
      {rows.map((p) => (
        <div className={`cash-row ${p.status === "void" ? "voided" : ""}`} key={p.id} data-testid={`lab-payment-row-${p.id}`}>
          <span className="cash-dir out"><WalletCards size={14} /></span>
          <div className="cash-label"><b>{p.lab_name}</b><small>{p.date} · {p.method}{p.from_drawer ? " · from drawer" : ""}{p.notes && ` · ${p.notes}`}{p.void_reason && ` · void: ${p.void_reason}`}</small></div>
          <span className="amount">{inr(p.amount)}</span>
          {can("payments:void") && p.status === "active" ? <button className="icon-btn" onClick={() => voidIt(p)} data-testid={`void-lab-payment-${p.id}`} title="Void"><Ban size={15} /></button> : <span />}
        </div>
      ))}
    </section>
  );
}

export default function Laboratory() {
  const can = useCan();
  const [tab, setTab] = useState("cases");
  const [status, setStatus] = useState("");
  const [modal, setModal] = useState("");
  const [lab, setLab] = useState("");
  const summary = useLoad("/lab/summary", { labs: [], total_outstanding: 0 });
  const cases = useLoad(`/lab/cases?status=${status}&lab_name=${encodeURIComponent(lab)}`, []);
  const payments = useLoad(`/lab/payments?lab_name=${encodeURIComponent(lab)}`, []);
  const refresh = () => { setModal(""); summary.reload(); cases.reload(); payments.reload(); };
  return (
    <div className="content">
      <PageHeading eyebrow="LABORATORY LEDGER" title="Every crown," accent="every rupee owed." sub="Charges come from lab cases; what you owe each lab is charges minus payments.">
        {can("lab:cases") && <button className="secondary" onClick={() => setModal("case")} data-testid="add-lab-case-button"><Plus size={16} /> New lab case</button>}
        {can("lab:pay") && <button className="primary" onClick={() => setModal("pay")} data-testid="add-lab-payment-button"><WalletCards size={17} /> Pay a lab</button>}
      </PageHeading>
      <ErrorBox error={summary.error} testid="lab-error" />
      <section className="lab-cards">
        <button className={!lab ? "lab-card active" : "lab-card"} onClick={() => setLab("")} data-testid="lab-filter-all"><span>All labs</span><b data-testid="lab-total-outstanding">{inr(summary.data.total_outstanding)}</b><small>owed in total</small></button>
        {summary.data.labs.map((l) => (
          <button key={l.lab_name} className={lab === l.lab_name ? "lab-card active" : "lab-card"} onClick={() => setLab(l.lab_name)} data-testid={`lab-card-${l.lab_name}`}>
            <span>{l.lab_name}</span><b className={l.outstanding > 0 ? "owed" : ""}>{inr(l.outstanding)}</b><small>{l.open_cases} open · charges {inr(l.charges)} · paid {inr(l.paid)}</small>
          </button>
        ))}
      </section>
      <div className="tabs-row">
        <div className="tabs">{[["cases", "Cases", FlaskConical], ["payments", "Payments", WalletCards]].map(([k, l, Icon]) => <button key={k} className={tab === k ? "tab active" : "tab"} onClick={() => setTab(k)} data-testid={`lab-tab-${k}`}><Icon size={15} /> {l}</button>)}</div>
        {tab === "cases" && <select className="status-select" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="lab-status-filter"><option value="">All statuses</option><option value="sent">Sent</option><option value="in_progress">In progress</option><option value="received">Received</option><option value="fitted">Fitted</option><option value="remake">Remake</option><option value="cancelled">Cancelled</option></select>}
      </div>
      {tab === "cases" ? <LabCaseList rows={cases.data} onChanged={refresh} /> : <LabPayments rows={payments.data} onChanged={refresh} />}
      {modal === "case" && <LabCaseForm labs={summary.data.labs} onClose={() => setModal("")} onSaved={refresh} />}
      {modal === "pay" && <LabPaymentForm labs={summary.data.labs} defaultLab={lab} onClose={() => setModal("")} onSaved={refresh} />}
    </div>
  );
}
